/**
 * Thin FE wrappers for `/api/v1/users/*` (the ADMIN allowlist admin API).
 *
 * Mirrors `api/customers.ts`: every function calls `apiFetch` and hands the
 * raw body through the matching zod schema from `@dragonis-cdr/shared`'s
 * `users` namespace. Validation here is not paranoid defence against the
 * server — it is defence against shape drift between FE and API, and it
 * produces a much better error at the edge than a deep `undefined` later in
 * a component.
 *
 * Note on list-response shape (contrast with `customers.ts`):
 *   `customers.ts` has to `.extend()` the shared list schema because the
 *   shared `CustomerListResponse` only models `{items, total}` while the API
 *   returns `{items, total, page, pageSize}`. The shared `UserListResponse`
 *   (T-059) already models all four fields and the T-069 `GET /users`
 *   handler returns exactly that shape, so here the parse is direct — no
 *   local extension is needed.
 *
 * CSRF: mutations (POST/PATCH/DELETE) carry the double-submit token. We do
 *   NOT set the header here — `apiFetch` attaches `X-CSRF-Token` on any
 *   non-GET/HEAD method from the getter `AuthProvider` registers (see
 *   `api/client.ts`). This is byte-for-byte the same mechanism `customers.ts`
 *   relies on; adding a header here would duplicate (and risk diverging from)
 *   that single source of truth.
 */
import { users } from '@dragonis-cdr/shared';

import { apiFetch } from './client';

export type UserRecord = users.UserRecord;
export type UserRole = users.UserRole;
export type UserCreateRequest = users.UserCreateRequest;
export type UserUpdateRequest = users.UserUpdateRequest;
export type UserListResponse = users.UserListResponse;

export interface ListUsersParams {
  page?: number;
  pageSize?: number;
}

/**
 * Build a `?page=&pageSize=` query string, dropping non-finite/undefined
 * values so an unset param is simply absent (the API applies its own
 * defaults). Identical idiom to `customers.ts::buildQuery`.
 */
function buildQuery(params: Record<string, number | undefined>): string {
  const entries = Object.entries(params).filter((entry): entry is [string, number] => {
    const value = entry[1];
    return typeof value === 'number' && Number.isFinite(value);
  });
  if (entries.length === 0) return '';
  const usp = new URLSearchParams();
  for (const [key, value] of entries) {
    usp.set(key, String(value));
  }
  return `?${usp.toString()}`;
}

export async function listUsers(params: ListUsersParams = {}): Promise<UserListResponse> {
  const query = buildQuery({
    page: params.page,
    pageSize: params.pageSize,
  });
  const body = await apiFetch<unknown>(`/users${query}`);
  return users.UserListResponse.parse(body);
}

/**
 * POST /api/v1/users — invite a user by email + role. The server lower-cases
 * the email and creates the row with `entraObjectId=null` (INVITED state,
 * ADR-011). A duplicate email is a 409 `/errors/conflict` the caller surfaces
 * inline.
 */
export async function createUser(input: UserCreateRequest): Promise<UserRecord> {
  const body = await apiFetch<unknown>('/users', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return users.UserRecord.parse(body);
}

/**
 * PATCH /api/v1/users/:id — change role and/or the disabled flag. The
 * last-admin guard is server-side and surfaces as a 409 `/errors/conflict`
 * ("cannot remove the last administrator") the caller surfaces inline.
 */
export async function updateUser(
  id: string,
  input: UserUpdateRequest,
): Promise<UserRecord> {
  const body = await apiFetch<unknown>(`/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return users.UserRecord.parse(body);
}

/**
 * DELETE is a soft delete server-side — the row is retained with
 * `disabled=true` (FK-safe; never hard-deleted). The endpoint returns 204.
 * The same last-admin guard as PATCH applies and can surface a 409.
 */
export async function deleteUser(id: string): Promise<void> {
  await apiFetch<void>(`/users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
